import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars as v } from '@ovasabi/ui-minimal/tokens'
import { MinimalButton, MinimalEmptyState } from '@ovasabi/ui-minimal'
import {
  DishRow,
  FilterPills,
  PageHeader,
  Screen,
  SearchField,
  SectionHeading,
  Skeleton,
  StatusDot,
  type PillOption,
} from '../../components/chow'
import { ExploreState } from '../../components/chow/ExploreCard'
import {
  ChatIcon,
  CheckIcon,
  ChefHatIcon,
  CopyIcon,
  PinIcon,
  PlateIcon,
  StarIcon,
  UtensilsIcon,
} from '../../components/icons'
import { KitchenBadge } from '../../components/chow/KitchenBadge'
import { ChowThumb } from './ChowThumb'
import { chefPhoto } from './dishImages'
import { useAuth } from './auth/AuthContext'
import { recordPendingAction } from './auth/pendingAction'
import { useFavourites } from './favourites'
import { addDishToCart } from './cart'
import {
  dishCardEta,
  useUiDishes,
  type UiDish,
} from './projections'
import { usePublicChefs, type PublicChef } from '../../lib/discover'
import {
  useChefMenuSnapshot,
  useChowProfileSnapshot,
  useDiscoverChefSnapshot,
  useVendorProfileSnapshot,
} from '../../generated/prototypeRuntime'
import { useBackNav, useChooseChow } from './ChooseChowContext'
import { canonicalSlug, slugify } from '../../lib/slug'
import { shareKitchen } from './kitchenShare'
import { chefs, type Chef } from './content'
import type { ChowStatus } from '../../components/chow'
import { useProjectionReady } from '../../lib/projectionHealth'
import { displayTitle } from '../../lib/displayName'

const Style = {
  CoverHero: styled.div`
    position: relative;
    background: linear-gradient(180deg, #FFFDFB 0%, #FFF7ED 100%);
    border: none;
    border-radius: 28px;
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);

    @media (min-width: 640px) {
      padding: 30px 32px;
      gap: 22px;
    }
  `,
  ProfileRow: styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (min-width: 640px) {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      align-items: start;
      gap: 24px;
    }
  `,
  AvatarBox: styled.div`
    position: relative;
    width: 88px;
    height: 88px;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border: 3px solid #fff;
    background: #fff;
    flex-shrink: 0;

    @media (min-width: 640px) {
      width: 120px;
      height: 120px;
      border-radius: 28px;
      border-width: 4px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  `,
  HeroContent: styled.div`
    display: grid;
    gap: 8px;
    min-width: 0;
  `,
  TitleRow: styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;

    h1 {
      margin: 0;
      font-size: clamp(1.4rem, 2.5vw, 1.85rem);
      font-weight: 850;
      letter-spacing: -0.02em;
      color: ${v.color.textPrimary};
    }
  `,
  MetaRow: styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: ${v.typography.captionSize};
    color: ${v.color.textSecondary};

    span.badge-pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      background: rgba(255, 255, 255, 0.95);
      border: 1px solid rgba(226, 219, 210, 0.6);
      padding: 4px 10px;
      border-radius: ${v.radius.pill};
      font-weight: 600;
      font-size: 0.8rem;
      color: ${v.color.textPrimary};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    }
  `,
  BioText: styled.p`
    margin: 4px 0 0;
    font-size: 0.88rem;
    line-height: 1.5;
    color: ${v.color.textSecondary};
    max-width: 65ch;
  `,
  ActionBar: styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 6px;

    @media (max-width: 480px) {
      width: 100%;
    }
  `,
  ActionBtn: styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 8px 16px;
    min-height: 40px;
    border-radius: ${v.radius.pill};
    border: 1px solid rgba(215, 205, 192, 0.8);
    background: #fff;
    color: ${v.color.textPrimary};
    font-size: ${v.typography.captionSize};
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
    transition: transform 120ms ease, background 120ms ease, box-shadow 120ms ease;

    @media (max-width: 480px) {
      flex: 1 1 calc(50% - 6px);
      min-height: 44px;
    }

    &:hover {
      background: #fdfaf6;
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
    }
    &:active {
      transform: translateY(0);
    }
    &:focus-visible {
      outline: 2px solid ${v.color.brand};
      outline-offset: 2px;
    }
  `,
  PlanStrip: styled.div`
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(230, 218, 204, 0.6);
    border-radius: 20px;
    padding: 14px 20px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

    @media (max-width: 560px) {
      padding: 14px 16px;
      gap: 12px;
      button {
        width: 100%;
        justify-content: center;
      }
    }

    .plan-content {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }

    .plan-icon {
      flex-shrink: 0;
      width: 38px;
      height: 38px;
      border-radius: 12px;
      background: #FFF0DD;
      color: #c2410c;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .plan-text {
      display: grid;
      gap: 2px;
      strong {
        font-size: 0.92rem;
        font-weight: 800;
        color: #1a1a1a;
      }
      p {
        margin: 0;
        font-size: 0.82rem;
        color: #666;
        line-height: 1.35;
      }
    }
  `,
  MenuShelf: styled.div`
    display: grid;
    gap: 12px;
  `,
  MenuRail: styled.div`
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 6px;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `,
  MenuTile: styled(Link)`
    flex: 0 0 240px;
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
    background: ${v.color.bgSurface};
    border: 1px solid ${v.color.borderSubtle};
    border-radius: 16px;
    overflow: hidden;
    box-shadow: ${v.shadow.subtle};
    transition: transform 140ms ease;

    &:hover {
      transform: translateY(-2px);
      border-color: ${v.color.brand};
    }
  `,
  MenuCover: styled.div`
    width: 100%;
    aspect-ratio: 16 / 9;
    background: ${v.color.bgSurfaceAlt};
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  `,
  MenuTileBody: styled.div`
    padding: 10px 12px;
    display: grid;
    gap: 2px;
    strong {
      font-size: 0.9rem;
      font-weight: 800;
    }
    span {
      font-size: 0.78rem;
      color: ${v.color.textSecondary};
    }
  `,
  /* Heading, search, pills and grid are one section: a single gap between
     them, instead of each picking up the page body's gap plus its own margin
     (which left the pills floating far from the heading and flush against the
     list). */
  DishesSection: styled.section`
    display: grid;
    gap: 12px;
    min-width: 0;
    > * { min-width: 0; }
  `,
  DishesHeader: styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (min-width: 640px) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }
  `,
  SearchWrapper: styled.div`
    width: 100%;
    @media (min-width: 640px) {
      max-width: 300px;
    }
    input {
      min-height: 44px;
    }
  `,
  DishesGrid: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${v.space.md};

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: ${v.space.md};
    }

    @media (min-width: 960px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: ${v.space.lg};
    }
  `,
  SkeletonHero: styled.div`
    background: linear-gradient(180deg, #FFFDFB 0%, #FFF7ED 100%);
    border-radius: 28px;
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);

    @media (min-width: 640px) {
      flex-direction: row;
      align-items: flex-start;
      gap: 24px;
      padding: 30px 32px;
    }
  `,
  SkeletonLines: styled.div`
    display: grid;
    gap: 10px;
    flex: 1;
    min-width: 0;
  `,
}

/** Filter groups. Soups and swallows are one shelf; normalizeCategory keeps them apart. */
const filterGroup = (category: string): string => (category === 'swallow' ? 'soups' : category)

const FILTER_LABELS: Record<string, string> = {
  rice: 'Rice',
  soups: 'Soups & Swallows',
  protein: 'Protein',
  grill: 'Grill & Suya',
  chops: 'Small Chops',
  breakfast: 'Breakfast',
  drinks: 'Drinks',
}

const FILTER_ORDER = ['rice', 'soups', 'protein', 'grill', 'chops', 'breakfast', 'drinks']

const filterLabel = (group: string): string =>
  FILTER_LABELS[group] ?? group.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

/** Search earns its place once a kitchen lists more dishes than fit on a screen. */
const SEARCH_THRESHOLD = 6

/** The page's shape while the kitchen resolves, so nothing jumps when it lands. */
const KitchenSkeleton = () => (
  <>
    <Style.SkeletonHero aria-busy="true" aria-label="Loading kitchen">
      <Skeleton $width="96px" $height="96px" $radius="24px" />
      <Style.SkeletonLines>
        <Skeleton $width="55%" $height="28px" $radius="8px" />
        <Skeleton $width="40%" $height="22px" $radius="999px" />
        <Skeleton $width="75%" $height="14px" $radius="4px" />
        <Skeleton $width="260px" $height="40px" $radius="999px" />
      </Style.SkeletonLines>
    </Style.SkeletonHero>
    <ExploreState loading skeletonCount={3}>{null}</ExploreState>
  </>
)

interface KitchenProfile {
  id: string
  name: string
  cuisine: string
  area: string
  open: boolean
  image?: string
  glyph: string
  badge: string
  status: ChowStatus
  priceFrom: number
  rating: number
  reviewCount: number
  bio: string
}

const menuRecordsSafe = (records?: readonly Record<string, unknown>[]): readonly Record<string, unknown>[] =>
  records || []

export const KitchenPage = () => {
  const { kitchenId } = useParams<{ kitchenId: string }>()
  const navigate = useNavigate()
  const back = useBackNav()
  const { status } = useAuth()
  const { profileId, isChef } = useChooseChow()

  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [qty, setQty] = useState<Record<string, number>>({})
  const [copied, setCopied] = useState(false)

  const { chefs: rawPublicChefs, loading: chefsLoading } = usePublicChefs(50)
  const publicChefs = rawPublicChefs ?? []
  const discoverChefsSnap = useDiscoverChefSnapshot() as { records?: readonly Record<string, unknown>[] }
  const discoverReady = useProjectionReady('discovery', 'discover_chefs')
  const vendorsReady = useProjectionReady('profile', 'vendor_profiles')
  const vendorProfilesSnap = useVendorProfileSnapshot() as { records?: readonly Record<string, unknown>[] }
  const chowProfilesSnap = useChowProfileSnapshot() as { records?: readonly Record<string, unknown>[] }
  const { records: dishes, loading: dishesLoading } = useUiDishes()
  const menus = useChefMenuSnapshot() as { records?: readonly Record<string, unknown>[] }
  const favourites = useFavourites(profileId)

  const menuRecords = menuRecordsSafe(menus?.records)

  // Resolve target kitchen across live discover projection, vendor profiles, public catalog, and seed fallbacks
  const resolvedKitchen = useMemo<KitchenProfile | undefined>(() => {
    if (!kitchenId) return undefined
    const clean = kitchenId.trim().toLowerCase()
    const targetTail = clean.length >= 8 ? clean.slice(-8) : clean

    // Live open map for realtime open/closed overlay
    const liveOpenMap = new Map<string, boolean>()
    for (const r of discoverChefsSnap.records || []) {
      const isOpen = Boolean(r.open)
      if (r.id) liveOpenMap.set(String(r.id).toLowerCase(), isOpen)
      if (r.profile_id) liveOpenMap.set(String(r.profile_id).toLowerCase(), isOpen)
      if (r.vendor_id) liveOpenMap.set(String(r.vendor_id).toLowerCase(), isOpen)
      if (r.name) liveOpenMap.set(String(r.name).trim().toLowerCase(), isOpen)
    }

    // Minimum active dish price per chef
    const minPriceMap = new Map<string, number>()
    for (const d of dishes) {
      if (d.status === 'hidden' || !d.price || d.price <= 0) continue
      const chefName = d.chef.trim().toLowerCase()
      const chefId = d.chefId ? d.chefId.toLowerCase() : ''
      if (chefName) {
        const cur = minPriceMap.get(chefName) ?? Infinity
        minPriceMap.set(chefName, Math.min(cur, d.price))
      }
      if (chefId) {
        const cur = minPriceMap.get(chefId) ?? Infinity
        minPriceMap.set(chefId, Math.min(cur, d.price))
      }
    }

    // 1. Authoritative public chefs HTTP catalog (deduplicated & filtered)
    const publicCandidates: KitchenProfile[] = publicChefs
      .filter((c) => {
        const name = (c.name || '').trim().toLowerCase()
        return Boolean(name) && !name.includes('deleted') && c.badge !== 'revoked'
      })
      .map((c: PublicChef, index: number): KitchenProfile => {
        const isOpen =
          liveOpenMap.get((c.id || '').toLowerCase()) ??
          liveOpenMap.get(c.name.trim().toLowerCase()) ??
          c.open
        const backendPrice = c.price_from_minor && c.price_from_minor > 0
          ? Math.round(c.price_from_minor / 100)
          : 0
        const localPrice =
          minPriceMap.get((c.id || '').toLowerCase()) ??
          minPriceMap.get(c.name.trim().toLowerCase()) ??
          0
        const priceFrom = backendPrice > 0 ? backendPrice : localPrice

        return {
          id: c.id || `public-chef-${index}`,
          name: c.name,
          cuisine: c.cuisine,
          area: c.area,
          open: isOpen,
          image: chefPhoto(c.avatar, c.cuisine, c.name),
          glyph: '👩🏾‍🍳',
          badge: c.badge || '',
          status: (isOpen ? 'open' : 'closed') as ChowStatus,
          priceFrom,
          rating: typeof c.rating === 'number' ? c.rating : 0,
          reviewCount: typeof c.reviews === 'number' ? c.reviews : 0,
          bio: `${c.cuisine || 'Home-cooked specialties'} prepared fresh to order in ${c.area || 'Abuja'}.`,
        }
      })

    // 2. Live discover_chefs projection (Hermes live stream, sanitized)
    const discoverCandidates: KitchenProfile[] = (discoverChefsSnap.records || [])
      .filter((c) => {
        const name = String(c.name || '').trim().toLowerCase()
        const badge = String(c.badge || '').trim().toLowerCase()
        const id = String(c.id || '').toLowerCase()
        if (!name || name === 'kitchen' || name.includes('deleted')) return false
        if (badge === 'revoked') return false
        if (id.includes('-prototype')) return false
        return true
      })
      .map((c, index): KitchenProfile => {
        const dId = String(c.profile_id || c.id || `disc-chef-${index}`)
        const dName = displayTitle(c.name) || 'Kitchen'
        const priceFrom = minPriceMap.get(dId.toLowerCase()) ?? minPriceMap.get(dName.trim().toLowerCase()) ?? 0

        return {
          id: dId,
          name: dName,
          cuisine: String(c.cuisine || ''),
          area: String(c.area || ''),
          open: Boolean(c.open),
          image: chefPhoto(String(c.avatar || ''), String(c.cuisine || ''), dName),
          glyph: '👩🏾‍🍳',
          badge: String(c.badge || ''),
          status: (c.open ? 'open' : 'closed') as ChowStatus,
          priceFrom,
          rating: Number(c.rating) || 0,
          reviewCount: Number(c.reviews) || 0,
          bio: `${String(c.cuisine || 'Home-cooked specialties')} prepared fresh to order in ${String(c.area || 'Abuja')}.`,
        }
      })

    // 3. Vendor + Chow profiles snapshot (sanitized)
    const chowMap = new Map((chowProfilesSnap.records || []).map((p) => [String(p.id ?? ''), p]))
    const vendorCandidates: KitchenProfile[] = (vendorProfilesSnap.records || [])
      .filter((v) => {
        const vStatus = String(v.verification_status ?? '').toLowerCase()
        const vName = String(v.name ?? v.display_name ?? '').toLowerCase()
        if (vStatus === 'revoked') return false
        if (vName.includes('deleted')) return false
        return true
      })
      .map((v, index): KitchenProfile => {
        const pId = String(v.profile_id ?? v.id ?? '')
        const profile = chowMap.get(pId)
        const name = displayTitle(profile?.display_name ?? v.name) || 'Kitchen'
        const cuisine = String(v.cuisine ?? profile?.cuisine ?? '')
        const avatar = String(v.avatar_image_url ?? profile?.avatar_url ?? '')
        const isOpen = Boolean(v.is_open)
        const priceFrom = minPriceMap.get(pId.toLowerCase()) ?? minPriceMap.get(name.trim().toLowerCase()) ?? 0

        return {
          id: pId || `vendor-${index}`,
          name,
          cuisine,
          area: '',
          open: isOpen,
          image: chefPhoto(avatar, cuisine, name),
          glyph: '👩🏾‍🍳',
          badge: String(v.badge_tier ?? ''),
          status: (isOpen ? 'open' : 'closed') as ChowStatus,
          priceFrom,
          rating: 0,
          reviewCount: 0,
          bio: `${cuisine || 'Home-cooked specialties'} prepared fresh to order.`,
        }
      })

    // 4. Fallback curated seed chefs
    const seedCandidates: KitchenProfile[] = chefs.map((c: Chef, index: number): KitchenProfile => ({
      id: `seed-chef-${index}`,
      name: c.name,
      cuisine: c.cuisine,
      area: c.area,
      open: c.status === 'open',
      image: chefPhoto(c.image, c.cuisine, c.name),
      glyph: c.glyph || '👩🏾‍🍳',
      badge: c.badge || '',
      status: c.status,
      priceFrom: c.priceFrom,
      rating: typeof c.rating === 'number' ? c.rating : 0,
      reviewCount: 0,
      bio: `${c.name} prepares fresh homemade food in ${c.area || 'your area'}.`,
    }))

    const candidates: KitchenProfile[] = [
      ...publicCandidates,
      ...discoverCandidates,
      ...vendorCandidates,
      ...seedCandidates,
    ]

    return candidates.find((c) => {
      const id = c.id.toLowerCase()
      if (id === clean) return true
      if (canonicalSlug(c.name, c.id).toLowerCase() === clean) return true
      if (slugify(c.name).toLowerCase() === clean) return true
      if (targetTail && (id.startsWith(targetTail) || id.endsWith(targetTail))) return true
      return false
    })
  }, [
    kitchenId,
    discoverChefsSnap.records,
    publicChefs,
    vendorProfilesSnap.records,
    chowProfilesSnap.records,
    dishes,
  ])

  // Stale-While-Revalidate: preserve the active kitchen synchronously across
  // background sweeps and network retries so mobile view never flickers to "not found".
  const lastKnownKitchen = useRef<KitchenProfile | undefined>(undefined)
  if (resolvedKitchen) {
    lastKnownKitchen.current = resolvedKitchen
  }
  const kitchen = resolvedKitchen ?? lastKnownKitchen.current

  // Every dish this kitchen lists, before the page's own filters
  const allKitchenDishes = useMemo(() => {
    if (!kitchen) return []
    const cleanKitchenName = kitchen.name.trim().toLowerCase()
    return dishes.filter((d) => {
      const chefName = d.chef.trim().toLowerCase()
      return (
        chefName === cleanKitchenName ||
        Boolean(d.chefId && d.chefId.toLowerCase() === kitchen.id.toLowerCase())
      )
    })
  }, [dishes, kitchen])

  // Pills for the categories this kitchen actually sells — a fixed list offered
  // seven shelves to a kitchen with one dish, most of them leading nowhere.
  const filterOptions = useMemo<readonly PillOption<string>[]>(() => {
    const groups = [...new Set(allKitchenDishes.map((d) => filterGroup(d.category)))]
    groups.sort((a, b) => {
      const ia = FILTER_ORDER.indexOf(a)
      const ib = FILTER_ORDER.indexOf(b)
      return (ia < 0 ? FILTER_ORDER.length : ia) - (ib < 0 ? FILTER_ORDER.length : ib) || a.localeCompare(b)
    })
    return [{ value: 'all', label: 'All Dishes' }, ...groups.map((g) => ({ value: g, label: filterLabel(g) }))]
  }, [allKitchenDishes])

  const kitchenDishes = useMemo(() => {
    const term = query.trim().toLowerCase()
    return allKitchenDishes.filter((d) => {
      if (filter !== 'all' && filterGroup(d.category) !== filter) return false
      if (term && !d.name.toLowerCase().includes(term)) return false
      return true
    })
  }, [allKitchenDishes, filter, query])

  // Filter kitchen's menus
  const kitchenMenus = useMemo(() => {
    if (!kitchen) return []
    const cleanKitchenName = kitchen.name.trim().toLowerCase()
    return menuRecords.filter((m: Record<string, unknown>) => {
      const mChef = String(m.chef ?? '').trim().toLowerCase()
      const mProfileId = String(m.chef_profile_id ?? '').toLowerCase()
      return (
        mChef === cleanKitchenName ||
        (mProfileId && mProfileId === kitchen.id.toLowerCase())
      )
    })
  }, [menuRecords, kitchen])

  const handleShare = async () => {
    if (!kitchen) return
    const outcome = await shareKitchen({ id: kitchen.id, name: kitchen.name, cuisine: kitchen.cuisine, area: kitchen.area })
    if (outcome === 'copied') {
      setCopied(true)
      setTimeout(() => setCopied(false), 2400)
    }
  }

  const handleAddToCart = (dish: UiDish, quantity: number) => {
    if (status !== 'authenticated' || !profileId) {
      recordPendingAction({
        type: 'add_to_cart',
        dish: {
          id: dish.id,
          name: dish.name,
          price: dish.price,
          chefId: dish.chefId,
          chef: dish.chef,
          category: dish.category,
          image: dish.image,
          glyph: dish.glyph,
        },
        quantity,
        returnTo: `/kitchen/${kitchen ? canonicalSlug(kitchen.name, kitchen.id) : kitchenId}`,
        timestamp: Date.now(),
      })
      navigate(`/login?returnTo=${encodeURIComponent(`/kitchen/${kitchen ? canonicalSlug(kitchen.name, kitchen.id) : kitchenId}`)}`)
      return
    }
    void addDishToCart(dish, quantity, profileId)
  }

  // "Not found" is only an answer once every source has had its say: the
  // public list AND the live kitchen stream. Deciding on the first one to land
  // flashed "We could not find this kitchen" before the real page appeared.
  const header = <PageHeader title="Kitchens" onBack={back ?? (() => navigate('/kitchens'))} accentDot />

  if (!kitchen && (chefsLoading || !discoverReady || !vendorsReady)) {
    return (
      <Screen header={header}>
        <KitchenSkeleton />
      </Screen>
    )
  }

  if (!kitchen) {
    return (
      <Screen header={header}>
        <MinimalEmptyState
          icon={<ChefHatIcon />}
          title="We could not find this kitchen"
          description="This kitchen may have moved or renamed their storefront. Explore other local kitchens nearby."
          action={
            <MinimalButton variant="primary" onClick={() => navigate('/kitchens')}>
              Browse all kitchens
            </MinimalButton>
          }
        />
      </Screen>
    )
  }

  const content = (
    <>
      <Style.CoverHero>
        <Style.ProfileRow>
          <Style.AvatarBox>
            <ChowThumb
              src={chefPhoto(kitchen.image, kitchen.cuisine, kitchen.name)}
              alt={kitchen.name}
              name={kitchen.name}
              category={kitchen.cuisine}
              isChef
              thumbnailWidth={384}
            />
          </Style.AvatarBox>
          <Style.HeroContent>
            <Style.TitleRow>
              <h1>{kitchen.name}</h1>
              <KitchenBadge tier={kitchen.badge} />
            </Style.TitleRow>
            <Style.MetaRow>
              <span className="badge-pill">
                <StatusDot status={kitchen.status} showLabel={false} />
                {kitchen.open ? 'Open now' : 'Pre-order only'}
              </span>
              {kitchen.cuisine && (
                <span className="badge-pill">
                  <UtensilsIcon size="sm" />
                  {kitchen.cuisine}
                </span>
              )}
              {kitchen.area && (
                <span className="badge-pill">
                  <PinIcon size="sm" />
                  {kitchen.area}
                </span>
              )}
              {kitchen.reviewCount > 0 && kitchen.rating > 0 ? (
                <span className="badge-pill" style={{ color: '#b45309' }}>
                  <StarIcon size="sm" fill="#f59e0b" />
                  {kitchen.rating.toFixed(1)} ({kitchen.reviewCount} {kitchen.reviewCount === 1 ? 'review' : 'reviews'})
                </span>
              ) : (
                <span className="badge-pill">
                  New kitchen
                </span>
              )}
            </Style.MetaRow>
            {kitchen.bio && <Style.BioText>{kitchen.bio}</Style.BioText>}
            <Style.ActionBar>
              <Style.ActionBtn
                type="button"
                onClick={() => navigate(`/inbox?kitchen=${encodeURIComponent(kitchen.id)}&name=${encodeURIComponent(kitchen.name)}`)}
              >
                <ChatIcon size="sm" />
                Message kitchen
              </Style.ActionBtn>
              <Style.ActionBtn type="button" onClick={handleShare}>
                {copied ? <CheckIcon size="sm" /> : <CopyIcon size="sm" />}
                {copied ? 'Link copied!' : 'Share kitchen'}
              </Style.ActionBtn>
            </Style.ActionBar>
          </Style.HeroContent>
        </Style.ProfileRow>

        <Style.PlanStrip>
          <div className="plan-content">
            <span className="plan-icon" aria-hidden="true">
              <ChefHatIcon size="sm" />
            </span>
            <div className="plan-text">
              <strong>Weekly Meal Plan</strong>
              <p>Add this kitchen’s meals to your week and collect each one when it suits you.</p>
            </div>
          </div>
          <MinimalButton size="sm" variant="secondary" onClick={() => navigate('/mealplan')}>
            Add to meal plan →
          </MinimalButton>
        </Style.PlanStrip>
      </Style.CoverHero>

      {kitchenMenus.length > 0 && (
        <Style.MenuShelf>
          <SectionHeading title="Curated Menus" />
          <Style.MenuRail>
            {kitchenMenus.map((m: Record<string, unknown>) => (
              <Style.MenuTile key={String(m.id)} to={`/dishes?menu=${encodeURIComponent(String(m.id))}`}>
                <Style.MenuCover>
                  <ChowThumb src={String(m.cover_image_url || '')} thumbnailWidth={384} name={String(m.title)} />
                </Style.MenuCover>
                <Style.MenuTileBody>
                  <strong>{String(m.title)}</strong>
                  <span>{String(m.description || 'Curated meal collection')}</span>
                </Style.MenuTileBody>
              </Style.MenuTile>
            ))}
          </Style.MenuRail>
        </Style.MenuShelf>
      )}

      <Style.DishesSection>
        <Style.DishesHeader>
          <SectionHeading title="Dishes & Menu" />
          {allKitchenDishes.length > SEARCH_THRESHOLD && (
            <Style.SearchWrapper>
              <SearchField
                placeholder={`Search ${kitchen.name}'s dishes…`}
                value={query}
                onChange={setQuery}
              />
            </Style.SearchWrapper>
          )}
        </Style.DishesHeader>

        {filterOptions.length > 2 && (
          <FilterPills
            ariaLabel="Filter kitchen dishes"
            value={filter}
            options={filterOptions}
            onChange={(val) => setFilter(val)}
          />
        )}

        {/* Zero dishes is only an answer once the catalogue has landed. */}
        <ExploreState loading={dishesLoading && allKitchenDishes.length === 0} skeletonCount={3}>
          {kitchenDishes.length === 0 ? (
            <MinimalEmptyState
              icon={<PlateIcon />}
              title={query || filter !== 'all' ? 'No dishes match' : 'No dishes listed yet'}
              description={
                query || filter !== 'all'
                  ? 'Try choosing another category or clearing your search.'
                  : `${kitchen.name} has not published any dishes yet. Message the kitchen to ask what they're cooking.`
              }
              action={
                query || filter !== 'all' ? (
                  <MinimalButton
                    variant="secondary"
                    onClick={() => {
                      setFilter('all')
                      setQuery('')
                    }}
                  >
                    Show all dishes
                  </MinimalButton>
                ) : undefined
              }
            />
          ) : (
            <Style.DishesGrid>
              {kitchenDishes.map((dish) => (
                <DishRow
                  key={dish.id}
                  name={dish.name}
                  chef={dish.chef}
                  chefGlyph={dish.chefGlyph}
                  glyph={
                    <ChowThumb
                      src={dish.image}
                      emoji={dish.glyph}
                      alt={dish.name}
                      name={dish.name}
                      category={dish.category}
                      thumbnailWidth={384}
                    />
                  }
                  price={dish.price}
                  qty={qty[dish.id] || 1}
                  onQty={(value) => setQty((prev) => ({ ...prev, [dish.id]: value }))}
                  to={`/dishes/${encodeURIComponent(dish.id)}`}
                  onFavourite={() => favourites.toggle(dish.id)}
                  favourite={favourites.isFavourite(dish.id)}
                  onAdd={!isChef ? () => {
                    handleAddToCart(dish, qty[dish.id] || 1)
                    setQty((prev) => ({ ...prev, [dish.id]: 1 }))
                  } : undefined}
                  stock={dish.stock}
                  status={dish.status}
                  rating={dish.rating}
                  etaLabel={dishCardEta(dish.cookTimeMinutes)}
                />
              ))}
            </Style.DishesGrid>
          )}
        </ExploreState>
      </Style.DishesSection>
    </>
  )

  // One shell for everyone. Anonymous visitors already get the app's top nav
  // from the catalog route; a second, public nav here stacked two brand bars.
  return (
    <Screen header={header}>
      {content}
    </Screen>
  )
}
